import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Button from '../../../components/Button/Button'
import { handleDeleteRequest } from '../../../utils/pendingRequestHelpers'
// import AppRoutes from '../../../routes/appRoutes'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import useScreen from '../../../hooks/useScreen'
import { ExclamationIcon } from '@heroicons/react/solid'
import { onGettedAssetLocationInfo } from '../../../utils/onGettedAssetLocationInfo'
import useFleet from '../../../hooks/useFleet'
import useAlert from '../../../hooks/useAlert'
import { handleOnCancel, handleOnDone } from '../../../utils/handleOnDone'
import { sendEventToIntercom } from '../../../helpers/util'
import useRequest from '../../../hooks/useRequest'
import { parse } from 'qs'
import useAuth from '../../../hooks/useAuth'
import useGlobalLoading from '../../../hooks/useGlobalLoading'
import { useNetworkStatus } from '../../../utils/networkStatus'
import { handleConfirmDiscard } from '../../../utils/handleDiscard'
import { updatePendingRequestAttempt } from '../../../../../indexedDb/pendingRequests'
import {
    trackPartsRequestCreatedOffline,
    trackRepairRequestCreatedOffline,
} from '../../../utils/sendGAAnalyticsEvent'
import useUploadFile from '../../../hooks/useUploadFile'
import { base64ToFile } from '../../../utils/processImagesHelper'

const maxAttemptCount = 4

const NeedsReviewWarningCard = ({ message, request }) => {
    const { isMobile } = useScreen()
    const { user } = useAuth()
    const { handleGetAsset } = useFleet()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const { changeLoading } = useGlobalLoading()
    const { handleUpdateFile } = useUploadFile()
    const { isOffline } = useNetworkStatus()

    const [haveDisableAttemps, setHaveDisableAttemps] = useState(
        request?.attemptCount
            ? request?.attemptCount >= maxAttemptCount
            : false,
    )

    const history = useHistory()
    const {
        handleCreateServiceRequest,
        handleCreatePartsRequest,
    } = useRequest()
    const isEstimate = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.isEstimate

    const onCreatedRequestSuccess = response => {
        const requestId = response?.data?.response?.salesforceId
        // const hasCreditReview = response?.data?.response?.creditReview

        // Leaving the if here in case the modal comes back
        // if (hasCreditReview) {
        //     handleChangeAlert({
        //         title: `${
        //             isEstimate ? 'Estimate' : 'Service'
        //         } Request Pending Credit Review`,
        //         message: `Thank you for your request.  It is currently undergoing a credit review and will be processed once the review is complete.`,
        //         textOk: 'View Request',
        //         onDone: () => {
        //             history?.replace(AppRoutes.viewServiceRequest(requestId))
        //             handleChangeOpen(false)
        //         },
        //         type: 'question',
        //     })
        // } else {

        handleChangeAlert({
            title: isEstimate
                ? 'Your request has been sent'
                : 'Your Repair Request has been sent',
            message: isEstimate
                ? 'Check back to follow the status of your estimate'
                : 'Check back to see the latest status',
            textOk: 'View Request',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            onDone: () => {
                handleOnDone(history, requestId, handleChangeOpen, 'Repair')
            },
            type: 'success',
        })
    }

    const successResubmitAction = () => {
        localStorage.setItem('successAction', 'resubmitted')
        localStorage.setItem('showSuccessMessage', 'true')
        window.dispatchEvent(new Event('storage'))
    }

    const onCreatedRequest = async (response, id) => {
        onCreatedRequestSuccess(response)
        successResubmitAction()
        handleDeleteRequest(id)
    }

    // This upload all the necessary images and link them to the request and upload to AWS
    const uploadImages = async completeRequest => {
        let request = completeRequest
        if (request?.images.length > 0) {
            let successIds = []
            let images = request?.images
            await Promise.all(
                images?.map(image => {
                    const file = base64ToFile(image.data, image.name)
                    const formData = new FormData()
                    formData.append('file', file)

                    // This makes that we wait for every upload before continuing
                    return new Promise((resolve, reject) => {
                        handleUpdateFile(
                            formData,
                            res => {
                                const imageId = res?.data?.filename
                                successIds.push(imageId)
                                resolve()
                            },
                            error => {
                                console.log('Error', error)
                                reject(error)
                            },
                        )
                    })
                }),
            )
            // Parts has 1 images and server can have multiple
            if (request?.type === 'Parts') {
                request.photoId = successIds[0]
            } else {
                request.photoIds = successIds
            }
        }
        return request
    }

    const getCompleteRequest = async (request, showType) => {
        const paramsToGetFleetAsset = {
            assetId: request?.equipmentSFId,
            companyId: user?.company?.id,
            getRequests: true,
        }
        return handleGetAsset(
            paramsToGetFleetAsset,
            response => {
                const locationInfo = onGettedAssetLocationInfo(response)
                if (showType === 'Repair') {
                    request.shipToAccountId =
                        response?.data?.asset?.shipto?.external_id
                }
                const updatedAssetInfo = {
                    ...request,
                    serviceLocationId: locationInfo.serviceLocationId,
                    isEstimate: false,
                    photoIds: [],
                    locationDetails: request?.locationDetails,
                }
                return updatedAssetInfo
            },
            error => console.log(error),
        )
    }

    const onCreatedPartRequestSuccess = async (response, id) => {
        successResubmitAction()
        const requestId = response?.data?.response?.request?.salesforceId
        sendEventToIntercom('successful-parts-quote-request')
        handleDeleteRequest(id)
        handleChangeAlert({
            title: 'Request Created',
            message: 'Check back to see the latest status',
            textOk: 'View Request',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            onDone: () => {
                handleOnDone(history, requestId, handleChangeOpen, 'Parts')
            },
            type: 'success',
        })
    }

    const onCreateRequestError = async () => {
        const { id, attemptCount } = request
        console.log(request)
        try {
            await updatePendingRequestAttempt(id, attemptCount)
        } catch (error) {
            console.log(error)
        }

        let message = 'Unable to sync request'
        if (request.attemptCount >= maxAttemptCount) {
            message = message + ', last attempted used'
            setHaveDisableAttemps(true)
        }
        handleChangeAlert({
            title: null,
            message: message,
            type: 'error',
            textOk: 'Ok',
            onDone: () => {
                handleChangeOpen(false)
            },
        })
        request.attemptCount = attemptCount + 1
    }

    const handleSubmit = async request => {
        changeLoading(true)
        const { id, showType, ...requestInfo } = request
        try {
            const completeRequestNoImages = await getCompleteRequest(
                requestInfo,
                showType,
            )
            const completeRequest = await uploadImages(completeRequestNoImages)
            if (showType === 'Repair') {
                await handleCreateServiceRequest(
                    completeRequest,
                    response => {
                        trackRepairRequestCreatedOffline(
                            response,
                            user?.external_contact_id,
                            request,
                        )
                        onCreatedRequest(response, id)
                    },
                    error => {
                        onCreateRequestError()
                        console.log(error)
                    },
                )
            } else if (showType === 'Parts') {
                await handleCreatePartsRequest(
                    completeRequest,
                    response => {
                        trackPartsRequestCreatedOffline(
                            response,
                            user?.external_contact_id,
                            request,
                        )
                        onCreatedPartRequestSuccess(response, id)
                    },
                    error => {
                        onCreateRequestError()
                        console.log(error)
                    },
                )
            } else {
                console.warn(`Unknown showType "${showType}" for request ${id}`)
            }
        } catch (error) {
            console.error(`Failed to process request with ID ${id}:`, error)
        }
        changeLoading(false)
    }

    return (
        <div
            className={`w-full h-full bg-yellowLight p-4 rounded-2xl border border-yellowBorder flex ${
                isMobile
                    ? 'flex-col items-start gap-y-3'
                    : 'items-center gap-x-3.5'
            }`}
        >
            <ExclamationIcon className="w-5 h-5 text-yellow" />
            <div className="text-textDarkerGray font-medium text-sm leading-relaxed">
                {message || (
                    <div className="space-y-2">
                        <span>
                            We encountered an issue syncing your request.
                        </span>
                        <br />
                        <span>How would you like to proceed with it?</span>
                    </div>
                )}
            </div>
            <div
                className={`flex ${
                    isMobile ? 'space-x-3 w-full' : 'ml-auto space-x-3'
                }`}
            >
                <Button
                    text="Discard Request"
                    variant="tertiary"
                    className="text-md"
                    onClick={() =>
                        handleConfirmDiscard(
                            request,
                            handleChangeAlert,
                            handleChangeOpen,
                            history,
                            user?.external_contact_id,
                            isOffline,
                        )
                    }
                />
                {!isOffline && !haveDisableAttemps && (
                    <Button
                        text="Submit Again"
                        variant="primary"
                        className="text-md"
                        onClick={() => handleSubmit(request)}
                        disabled={haveDisableAttemps}
                    />
                )}
            </div>
        </div>
    )
}

NeedsReviewWarningCard.propTypes = {
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    request: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
}

export default NeedsReviewWarningCard
